/**
 * Respond tab row classification (§8.8/§8.9). Extracted from the component so the open-status
 * set and the card-tone decision are testable — and so the status list has exactly one frontend
 * home, mirrored on the backend's AiResponseActionStatuses.Open.
 */

// Statuses that mean someone still has a decision or follow-up on this action. Mirrors the
// backend's AiResponseActionStatuses.Open exactly — Executed included, because execution
// persisted without a verification success is still in flight, not done.
export const OPEN_STATUSES = [
  "Requested", "Approved", "ExecuteFailed", "PartiallyExecuted", "Executed", "VerifyFailed",
];

export const hasOpenAction = (actions) =>
  (actions || []).some((a) => OPEN_STATUSES.includes(a.status));

/**
 * What a Respond card's left edge should say before anyone reads the row:
 * "open" — a decision is in flight; "applied" — a provider scan observes containment or a
 * verified action is on record (configuration only: a row can carry several actions across
 * providers, so it never says whether issued credentials still work);
 * "actionable" — an action is available and nothing has been done.
 */
export const cardTone = (row, actions) => {
  if (hasOpenAction(actions)) return "open";
  if (row.containmentState === "Contained" || (actions || []).some((a) => a.status === "Verified"))
    return "applied";
  return "actionable";
};

const fmtWhen = (iso) => new Date(iso).toLocaleString();

/**
 * What one verified response action says about credentials issued before it ran. Used only per
 * action, never for a row or an asset. Switches on the backend's effectivenessKind and never
 * infers a state from a missing timestamp. Returns { label, detail, basis }, or null when the
 * action is not Verified (the read-back has not confirmed anything to describe).
 *
 * "Contained" is the Immediate label and nothing else: an Entra disable is Windowed and reads
 * "Provider confirmed" even after its window has passed and a scan has observed it.
 */
export const effectivenessState = (action, now = Date.now()) => {
  if (!action || action.status !== "Verified") return null;
  const basis = action.effectivenessBasis || null;
  switch (action.effectivenessKind) {
    case "Immediate":
      return { label: "Contained", detail: null, basis };
    case "Windowed": {
      if (!action.effectiveAfterUtc) return { label: "Provider confirmed", detail: null, basis };
      if (new Date(action.effectiveAfterUtc).getTime() > now)
        return {
          label: "Provider confirmed",
          detail: `Credentials issued before this action may still work until at most ${fmtWhen(action.effectiveAfterUtc)}.`,
          basis,
        };
      if (!action.effectivenessObservedAt)
        return {
          label: "Provider confirmed",
          detail: "Credential expiry has passed. Axaris is awaiting a successful discovery scan to confirm that the identity remains disabled.",
          basis,
        };
      return {
        label: "Provider confirmed",
        detail: `Credential expiry has passed. A scan on ${fmtWhen(action.effectivenessObservedAt)} found the identity still disabled.`,
        basis,
      };
    }
    case null:
    case undefined:
      // Null kind: verified before Axaris recorded effectiveness. Its own honest state.
      return { label: "Provider confirmed", detail: "Recorded before Axaris tracked credential expiry.", basis: null };
    default:
      // "Unknown", and any kind this build does not recognise, fails closed.
      return { label: "Provider confirmed", detail: null, basis };
  }
};

// The technology a row's actions reach, derived from the action types themselves — that is the
// honest label: it names the provider the kill switch actually touches.
export const ACTION_TECH = {
  DisableEntraIdentity: "Entra",
  QuarantineAwsRole: "AWS",
  DisableAnthropicWorkspaceKeys: "Anthropic",
  DeleteOpenAiProjectKeys: "OpenAI",
  DisableGcpServiceAccount: "Google Cloud",
};

export const techsFor = (actionTypes) =>
  [...new Set((actionTypes || []).map((t) => ACTION_TECH[t]).filter(Boolean))];
